import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Spinner } from '@aigovops/design-system';
import { listRegisterSubscribers, listQuestionnaires, type RegisterSubscriberRow, type QuestionnaireListRow } from '../../lib/api';
import type { LifecycleSystem } from '@aigovops/core';
import { lifecycleDate } from './lifecycle-view';

/**
 * SellPanel — V85-6-3 (gap G4).
 *
 * Subscriptions and questionnaires are workspace-scoped in the schema, not
 * per-system (register_subscriptions has no system_id; a buyer subscribes to
 * the whole register). This panel is the real, honest shape of that data:
 * who is subscribed to THIS workspace's register (supplier-side view), the
 * actual list of pending/answered questionnaires (not just counts), a real
 * last-response date, a filter over both, and a share-link action for the
 * latest signed release.
 */
export function SellPanel({ slug, prefix, systems }: { slug: string; prefix: string; systems: LifecycleSystem[] }) {
  const [state, setState] = useState<{ subscribers: RegisterSubscriberRow[]; questionnaires: QuestionnaireListRow[]; loading: boolean; error: string | null }>({ subscribers: [], questionnaires: [], loading: true, error: null });
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let active = true;
    setState(s => ({ ...s, loading: true, error: null }));
    Promise.all([listRegisterSubscribers(slug), listQuestionnaires(slug)])
      .then(([subs, qs]) => { if (active) setState({ subscribers: subs.subscribers, questionnaires: qs.questionnaires, loading: false, error: null }); })
      .catch(err => { if (active) setState(s => ({ ...s, loading: false, error: err instanceof Error ? err.message : 'Unable to load buyer activity.' })); });
    return () => { active = false; };
  }, [slug, retryCount]);

  if (state.loading) return <Spinner label="Loading buyer activity" />;
  if (state.error) return <Alert variant="error"><p>Buyer activity could not load. {state.error}</p><Button variant="ghost" onClick={() => setRetryCount(c => c + 1)}>Retry</Button></Alert>;

  const term = query.trim().toLowerCase();
  const subscribers = state.subscribers.filter(s => !term || s.buyer_name.toLowerCase().includes(term));
  const pending = state.questionnaires.filter(q => !['approved', 'exported', 'archived'].includes(q.status));
  const answered = state.questionnaires.filter(q => ['approved', 'exported'].includes(q.status));
  const questionnaires = [...pending, ...answered]
    .filter(q => !term || q.title.toLowerCase().includes(term))
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  const lastResponseAt = answered.map(q => q.updated_at).sort().at(-1) ?? null;

  // Latest signed release across all systems — the artifact a buyer verifies.
  const latestRelease = systems
    .map(s => s.release)
    .filter((r): r is LifecycleSystem['release'] & { last_release_id: string; last_release_at: string } => !!r.last_release_id && !!r.last_release_at)
    .sort((a, b) => b.last_release_at.localeCompare(a.last_release_at))[0];
  const shareUrl = latestRelease ? `${window.location.origin}/verify/release/${encodeURIComponent(latestRelease.last_release_id)}` : null;

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable — link remains visible for manual copy */ }
  };

  return <div className="v75-stack">
    <Card><div className="v75-panel">
      <h2>Buyer activity</h2>
      <p><strong>{state.subscribers.length}</strong> active subscriber{state.subscribers.length === 1 ? '' : 's'} · <strong>{pending.length}</strong> pending questionnaire{pending.length === 1 ? '' : 's'} · Last response {lifecycleDate(lastResponseAt)}</p>
      <div className="v75-actions">
        <Button onClick={copyShareLink} disabled={!shareUrl}>{copied ? 'Link copied' : 'Copy public verification link'}</Button>
        <Link className="v75-action" to={`${prefix}/questionnaires`}>Open buyer inbox</Link>
      </div>
      {!shareUrl && <p className="v75-muted">No signed release yet — sign a release to get a public verification link.</p>}
    </div></Card>

    <div className="v75-filters"><div><label htmlFor="sell-search">Find a subscriber or questionnaire</label><input id="sell-search" type="search" value={query} onChange={e => setQuery(e.target.value)} /></div></div>

    <Card><div className="v75-panel">
      <h2>Subscribers</h2>
      {subscribers.length === 0 ? <p className="v75-muted">{term ? 'No subscribers match your search.' : 'No workspace is subscribed to your register yet.'}</p> : <ul className="v75-connections">
        {subscribers.map(s => <li key={s.subscription_id}>
          <div><strong>{s.buyer_name}</strong><small>Subscribed {lifecycleDate(s.created_at)} · {s.mode === 'pinned' ? 'Pinned to a release' : 'Tracks latest'} · {s.ack_sla_days}-day ack SLA</small></div>
        </li>)}
      </ul>}
    </div></Card>

    <Card><div className="v75-panel">
      <h2>Questionnaires</h2>
      {questionnaires.length === 0 ? <p className="v75-muted">{term ? 'No questionnaires match your search.' : 'No buyer questionnaires yet.'}</p> : <ul className="v75-connections">
        {questionnaires.map(q => <li key={q.questionnaire_id}>
          <div><Link to={`${prefix}/questionnaires/${encodeURIComponent(q.questionnaire_id)}`}><strong>{q.title}</strong></Link><small>{['approved', 'exported'].includes(q.status) ? `Answered ${lifecycleDate(q.updated_at)}` : `Updated ${lifecycleDate(q.updated_at)}`}{q.due_date ? ` · Due ${lifecycleDate(q.due_date)}` : ''}</small></div>
          <Badge className={`v75-status ${['approved', 'exported'].includes(q.status) ? 'success' : q.status === 'in_review' ? 'warning' : 'neutral'}`}>{q.status.replace('_', ' ')}</Badge>
        </li>)}
      </ul>}
    </div></Card>
  </div>;
}
